import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiError } from 'src/api/axiosClient/types';
import { Company } from 'src/constants/types';
import { RootState } from 'src/store';
import { AdminCompany, GetAdminCompaniesParams, OnboardingCompanyStatus } from '../types';
import { CompaniesState } from './types';

const initialState: CompaniesState = {
  loading: {
    getCurrentCompanyList: false,
    getAdminCompanyList: false,
    create: false,
    delete: false,
    getCompanyHasPaymentMethod: false,
    getStripeCompanyPortalLink: false,
    completeOnboarding: false,
    onboardingStatus: false,
  },
  currentCompanies: [],
  currentCompanyId: undefined,
  adminCompanyListFilter: {
    offset: 0,
    limit: 25,
    page: 0,
  },
  adminCompanyList: null,
  currentAdminCompany: null,
  currentAdminCompanyLoading: false,
  paymentMethodConfigured: null,
  onboardingStatus: null,
  error: null,
};

const companiesSlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    setLoading: (
      state,
      action: PayloadAction<Partial<Record<keyof CompaniesState['loading'], boolean>>>,
    ) => {
      state.loading = { ...state.loading, ...action.payload };
    },
    setCurrentCompanies: (state, action: PayloadAction<Company[]>) => {
      state.currentCompanies = action.payload;
    },
    setCurrentCompanyId: (state, action: PayloadAction<string>) => {
      state.currentCompanyId = action.payload;
    },
    setAdminCompanyListFilter(state, action: PayloadAction<Partial<GetAdminCompaniesParams>>) {
      state.adminCompanyListFilter = {
        ...state.adminCompanyListFilter,
        ...action.payload,
      };
    },
    setAdminCompanyList: (
      state,
      action: PayloadAction<{
        items: AdminCompany[];
        count: number;
      }>,
    ) => {
      state.adminCompanyList = action.payload;
    },
    setCurrentAdminCompany: (state, action: PayloadAction<AdminCompany | null>) => {
      state.currentAdminCompany = action.payload;
    },
    setCurrentAdminCompanyLoading: (state, action: PayloadAction<boolean>) => {
      state.currentAdminCompanyLoading = action.payload;
    },
    setError: (state, action: PayloadAction<ApiError>) => {
      state.error = action.payload;
    },
    setPaymentMethodConfigured: (state, action: PayloadAction<boolean>) => {
      state.paymentMethodConfigured = action.payload;
    },
    setOnboardingStatus: (state, action: PayloadAction<OnboardingCompanyStatus>) => {
      state.onboardingStatus = action.payload;
    },
    resetAdminCompanyList: state => {
      state.adminCompanyListFilter = initialState.adminCompanyListFilter;
      state.adminCompanyList = initialState.adminCompanyList;
    },
    resetError: state => {
      state.error = initialState.error;
    },
    resetSlice: () => {
      initialState;
    },
  },
});

export const isCompaniesLoading = (state: RootState) => state.companies.loading;

export const getCurrentCompanies = (state: RootState) => state.companies.currentCompanies;

export const getCurrentCompanyId = (state: RootState): string | undefined => {
  return state.companies.currentCompanyId || state.companies.currentCompanies?.[0]?.id;
};

export const getCurrentCompanyAvailabilityStatus = (state: RootState): string => {
  return state.companies.currentCompanyId && state.companies.currentCompanies
    ? state.companies.currentCompanies.find(c => c.id === state.companies.currentCompanyId)
        ?.availabilityStatus || ''
    : '';
};

export const getCurrentCompanyName = (state: RootState): string => {
  return state.companies.currentCompanyId && state.companies.currentCompanies
    ? state.companies.currentCompanies.find(c => c.id === state.companies.currentCompanyId)
        ?.name || ''
    : '';
};

export const getAdminCompanyList = (state: RootState) => state.companies.adminCompanyList;

export const getAdminCompanyById = (companyId: string) => (state: RootState) =>
  state.companies.adminCompanyList?.items.find(company => company.id === companyId);

export const getCompanyById = (companyId: string) => (state: RootState) =>
  state.companies.currentCompanies.find(company => company.id === companyId);

export const getPaymentMethodConfigured = (state: RootState) => state.companies.paymentMethodConfigured;

export const selectCurrentAdminCompany = (state: RootState) => state.companies.currentAdminCompany;

export const selectCurrentAdminCompanyLoading = (state: RootState) => state.companies.currentAdminCompanyLoading;

export const getCurrentAdminCompany = (state: RootState) =>
  state.companies.currentAdminCompany ||
  state.companies.adminCompanyList?.items.find(c => c.id === state.companies.currentCompanyId) ||
  null;

export const getOnboardingStatus = (state: RootState) => state.companies.onboardingStatus;

export const isCompaniesError = (state: RootState) => state.companies.error;

export const {
  setLoading,
  setCurrentCompanies,
  setCurrentCompanyId,
  setAdminCompanyListFilter,
  setAdminCompanyList,
  setCurrentAdminCompany,
  setCurrentAdminCompanyLoading,
  setError,
  setPaymentMethodConfigured,
  setOnboardingStatus,
  resetAdminCompanyList,
  resetError,
  resetSlice,
} = companiesSlice.actions;

export const companiesReducer = companiesSlice.reducer;
